<template>
  <section class="relative bg-black overflow-hidden -mb-1">
    <div
      class="relative w-full aspect-[768/1376] md:aspect-[20/7] overflow-hidden"
    >

      <!-- Mobile: imagen estática -->
      <picture class="absolute inset-0 w-full h-full block md:hidden">
        <img src="/logos/mobile1.jpeg" alt="Banner principal" class="w-full h-full object-cover object-center" draggable="false" />
      </picture>

      <!-- Desktop: slider -->
      <div
        ref="track"
        class="hidden md:flex h-full will-change-transform"
        :style="trackStyle"
      >
        <div
          v-for="(banner, i) in banners"
          :key="i"
          class="min-w-full h-full shrink-0"
        >
          <img
            :src="banner.src"
            :alt="banner.alt"
            class="w-full h-full object-cover object-center select-none"
            draggable="false"
          />
        </div>
      </div>

      <!-- Flechas minimalistas -->
      <button
        @click="prev"
        aria-label="Anterior"
        class="hidden md:flex absolute left-6 lg:left-10 top-1/2 -translate-y-1/2 z-30 w-12 h-12 items-center justify-center text-white/80 hover:text-white border border-white/30 hover:border-white/70 rounded-full backdrop-blur-md bg-white/5 hover:bg-white/15 transition-all duration-300 cursor-pointer group"
      >
        <svg class="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        @click="next"
        aria-label="Siguiente"
        class="hidden md:flex absolute right-6 lg:right-10 top-1/2 -translate-y-1/2 z-30 w-12 h-12 items-center justify-center text-white/80 hover:text-white border border-white/30 hover:border-white/70 rounded-full backdrop-blur-md bg-white/5 hover:bg-white/15 transition-all duration-300 cursor-pointer group"
      >
        <svg class="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      <!-- Dots minimalistas -->
      <div class="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 z-30 gap-2.5">
        <button
          v-for="(_, i) in banners"
          :key="i"
          @click="goTo(i)"
          :aria-label="`Banner ${i + 1}`"
          :class="[
            'h-1.5 rounded-full transition-all duration-500 cursor-pointer',
            current === i ? 'w-8 bg-white' : 'w-1.5 bg-white/40 hover:bg-white/70'
          ]"
        />
      </div>

    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'

const banners = [
  { src: '/logos/banner1.jpg', alt: 'Banner 1' },
  { src: '/logos/banner2.PNG', alt: 'Banner 2' },
]

const TOTAL     = banners.length
const THRESHOLD = 50

const track     = ref<HTMLElement | null>(null)
const current   = ref(0)
const offset    = ref(0)
const dragging  = ref(false)

const trackStyle = computed(() => ({
  transform:  `translateX(calc(-${current.value * 100}% + ${offset.value}px))`,
  transition: dragging.value ? 'none' : 'transform 0.5s ease',
}))

const goTo = (i: number) => { current.value = i }
const next = () => { current.value = (current.value + 1) % TOTAL }
const prev = () => { current.value = (current.value - 1 + TOTAL) % TOTAL }

// Auto-slide
let timer: ReturnType<typeof setInterval> | null = null
const startAuto = () => { timer = setInterval(next, 8000) }
const stopAuto  = () => { if (timer) { clearInterval(timer); timer = null } }

// Touch
let touchX = 0
let touchY = 0
let isHorizontal: boolean | null = null

const onTouchStart = (e: TouchEvent) => {
  touchX = e.touches[0].clientX
  touchY = e.touches[0].clientY
  offset.value = 0
  isHorizontal = null
  dragging.value = true
  stopAuto()
}
const onTouchMove = (e: TouchEvent) => {
  const dx = e.touches[0].clientX - touchX
  const dy = e.touches[0].clientY - touchY
  if (isHorizontal === null) isHorizontal = Math.abs(dx) > Math.abs(dy)
  if (!isHorizontal) return
  e.preventDefault()
  offset.value = dx
}
const onTouchEnd = () => {
  dragging.value = false
  const d = offset.value
  offset.value = 0
  if (d < -THRESHOLD) next()
  else if (d > THRESHOLD) prev()
  isHorizontal = null
  startAuto()
}

// Mouse drag
const onMouseDown = (e: MouseEvent) => {
  e.preventDefault()
  const startX = e.clientX
  offset.value = 0
  dragging.value = true
  stopAuto()
  const onMove = (ev: MouseEvent) => { offset.value = ev.clientX - startX }
  const onUp   = (ev: MouseEvent) => {
    dragging.value = false
    const d = ev.clientX - startX
    offset.value = 0
    if (d < -THRESHOLD) next()
    else if (d > THRESHOLD) prev()
    startAuto()
    window.removeEventListener('mousemove', onMove)
    window.removeEventListener('mouseup', onUp)
  }
  window.addEventListener('mousemove', onMove)
  window.addEventListener('mouseup', onUp)
}

onMounted(() => {
  startAuto()
  const el = track.value
  if (!el) return
  el.addEventListener('touchstart', onTouchStart, { passive: true })
  el.addEventListener('touchmove',  onTouchMove,  { passive: false })
  el.addEventListener('touchend',   onTouchEnd)
  el.addEventListener('mousedown',  onMouseDown)
})

onUnmounted(() => {
  stopAuto()
  const el = track.value
  if (!el) return
  el.removeEventListener('touchstart', onTouchStart)
  el.removeEventListener('touchmove',  onTouchMove)
  el.removeEventListener('touchend',   onTouchEnd)
  el.removeEventListener('mousedown',  onMouseDown)
})
</script>
